import React from "react";
import { useTranslation } from "react-i18next";
import Modal from "./Modal";
import { MODE_CONTENT, MODE_COLOR_VAR, LeagueMode } from "../pages/createLeague/modeContent";
import { useIsMobile } from "../hooks/useIsMobile";

export interface LeagueFact {
  label: string;
  value: React.ReactNode;
}

interface Chip {
  label: string;
  value: string;
}

interface Props {
  mode: LeagueMode;
  leagueFacts?: LeagueFact[];
  onClose: () => void;
}

export default function HowToPlayModal({ mode, leagueFacts, onClose }: Props) {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const content = MODE_CONTENT[mode];
  const color = MODE_COLOR_VAR[mode];
  const Icon = content.icon;

  const modeLabel = t(`${content.keyRoot}.label`);
  const summary = t(`howToPlay.${mode}.summary`);
  const steps = t(`howToPlay.${mode}.steps`, { returnObjects: true }) as string[];
  const chips = t(`howToPlay.${mode}.chips`, { returnObjects: true }) as Chip[];

  return (
    <Modal onClose={onClose}>
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "var(--color-surface)",
          borderRadius: 16,
          border: `1px solid color-mix(in srgb, ${color} 25%, var(--color-border))`,
          boxShadow: `0 0 0 1px color-mix(in srgb, ${color} 12%, transparent), 0 24px 64px var(--color-shadow)`,
          maxWidth: 480,
          width: isMobile ? "94%" : "90%",
          maxHeight: isMobile ? "92vh" : "88vh",
          overflowY: "auto",
        }}
      >
        <div
          style={{
            background: `linear-gradient(180deg, color-mix(in srgb, ${color} 18%, var(--color-surface)), var(--color-surface))`,
            borderRadius: "16px 16px 0 0",
            padding: isMobile ? "1.25rem 1.25rem 1.1rem" : "1.5rem 1.75rem 1.25rem",
            position: "relative",
          }}
        >
          <button
            onClick={onClose}
            aria-label={t("common.close")}
            style={{
              position: "absolute",
              top: isMobile ? "0.9rem" : "1.1rem",
              right: isMobile ? "0.9rem" : "1.1rem",
              width: 32,
              height: 32,
              borderRadius: 8,
              border: "1px solid var(--color-border)",
              background: "var(--color-surface)",
              color: "var(--color-text-muted)",
              cursor: "pointer",
              fontSize: "1.1rem",
              lineHeight: 1,
            }}
          >
            ×
          </button>
          <div style={{ display: "flex", alignItems: "center", gap: "0.85rem", marginBottom: "0.75rem" }}>
            <div
              style={{
                flexShrink: 0,
                width: 44,
                height: 44,
                borderRadius: 12,
                background: color,
                color: "var(--color-btn-primary-text)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Icon />
            </div>
            <div>
              <div
                style={{
                  fontFamily: "var(--font-mono)",
                  fontSize: "0.7rem",
                  fontWeight: 600,
                  textTransform: "uppercase",
                  letterSpacing: "0.1em",
                  color: "var(--color-text-muted)",
                  marginBottom: "0.15rem",
                }}
              >
                {t("howToPlay.eyebrow")}
              </div>
              <h3
                style={{
                  margin: 0,
                  fontFamily: "var(--font-display)",
                  fontSize: isMobile ? "1.3rem" : "1.6rem",
                  fontWeight: 700,
                  color: "var(--color-text)",
                }}
              >
                {modeLabel}
              </h3>
            </div>
          </div>
          <p style={{ margin: 0, fontSize: "0.92rem", color: "var(--color-text-muted)", lineHeight: 1.5 }}>
            {summary}
          </p>
        </div>

        <div style={{ padding: isMobile ? "1.25rem" : "1.5rem 1.75rem 1.75rem", display: "flex", flexDirection: "column", gap: "1.1rem" }}>
          <div style={{ display: "flex", flexDirection: "column", gap: "0.85rem" }}>
            {steps.map((step, i) => (
              <div key={i} style={{ display: "flex", gap: "0.85rem" }}>
                <span
                  style={{
                    flexShrink: 0,
                    width: 26,
                    height: 26,
                    borderRadius: "50%",
                    background: color,
                    color: "var(--color-btn-primary-text)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "0.8rem",
                    fontWeight: 700,
                  }}
                >
                  {i + 1}
                </span>
                <span style={{ fontSize: "0.9rem", color: "var(--color-text)", lineHeight: 1.5, paddingTop: "0.1rem" }}>
                  {step}
                </span>
              </div>
            ))}
          </div>

          {leagueFacts && leagueFacts.length > 0 && (
            <div style={{ background: "var(--color-surface-alt)", borderRadius: 12, padding: "1rem 1.1rem" }}>
              <div
                style={{
                  fontFamily: "var(--font-mono)",
                  fontSize: "0.68rem",
                  fontWeight: 600,
                  textTransform: "uppercase",
                  letterSpacing: "0.08em",
                  color,
                  marginBottom: "0.7rem",
                }}
              >
                {t("howToPlay.inThisLeague")}
              </div>
              {leagueFacts.map((fact, i) => (
                <div
                  key={i}
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    gap: "1rem",
                    padding: "0.3rem 0",
                    fontSize: "0.86rem",
                  }}
                >
                  <span style={{ color: "var(--color-text-muted)" }}>{fact.label}</span>
                  <span style={{ fontWeight: 600, textAlign: "right" }}>{fact.value}</span>
                </div>
              ))}
            </div>
          )}

          {chips?.length > 0 && (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: isMobile ? "1fr" : `repeat(${chips.length}, 1fr)`,
                gap: "0.6rem",
              }}
            >
              {chips.map((chip, i) => (
                <div
                  key={i}
                  style={{
                    border: "1px solid var(--color-border)",
                    borderRadius: 10,
                    padding: "0.6rem 0.75rem",
                  }}
                >
                  <div
                    style={{
                      fontFamily: "var(--font-mono)",
                      fontSize: "0.62rem",
                      fontWeight: 600,
                      textTransform: "uppercase",
                      letterSpacing: "0.06em",
                      color: "var(--color-text-muted)",
                      marginBottom: "0.2rem",
                    }}
                  >
                    {chip.label}
                  </div>
                  <div style={{ fontSize: "0.88rem", fontWeight: 700, color: "var(--color-text)" }}>
                    {chip.value}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
}
